// This module is aimed to support Storage encrypted data in localStorage as base64 with AES-CBC encryption.
// It's not for security purpose. The real purpose is to not showing the serialized data in plain-text.

import getEnv from './getEnv'
import { parse } from 'tldts'
import { IS_SELFHOST_DEPLOYMENT } from './deployment'

// storage Key in Json Web Key(JWK) format
const storageJwk: JsonWebKey = {
    alg: 'A256CBC',
    ext: true,
    k: 'ZQzzFCDui77b59cl0K67VJHr9DlLyoPfc505yNJVVJY',
    key_ops: ['encrypt', 'decrypt'],
    kty: 'oct',
}

const iv = new Uint8Array([
    233,
    220,
    122,
    172,
    45,
    54,
    151,
    11,
    197,
    211,
    8,
    49,
    63,
    129,
    21,
    192,
])

// Self-host serves every tenant from one origin under /app/:subDomain
// (path-based tenancy) — every browser storage API that's scoped only by
// origin (localStorage, IndexedDB, Cache Storage, BroadcastChannel) is
// shared across tenants there, so prefix its key/name with the current
// tenant's subdomain (already populated per-request into window.env, see
// server/routes/static.ts) to keep two locations' sessions/devices/caches
// from clobbering each other, whether opened in separate tabs or one after
// another in the same tab. Not localStorage-specific despite the name — the
// transform (a colon-prefixed string) applies equally to a Cache Storage
// name or a BroadcastChannel name; see pos.service.ts/httpRequest for those
// uses. Cloud already isolates tenants by real per-tenant origins (the
// browser partitions storage by origin on its own), so this is a no-op
// there.
export const scopedStorageKey = (key: string): string => {
    const subdomain = getEnv('REACT_APP_SUB_DOMAIN')
    return IS_SELFHOST_DEPLOYMENT && subdomain ? `${subdomain}:${key}` : key
}

let storageCryptoKey: CryptoKey | undefined

const LOCAL_WITHOUT_PWA = Boolean(getEnv('LOCAL_WITHOUT_PWA'))
const isIp = parse(window.location.href).isIp

const getStorageCryptoKey = async () => {
    if (storageCryptoKey === undefined) {
        storageCryptoKey = await window.crypto.subtle.importKey(
            'jwk',
            storageJwk,
            {
                name: 'AES-CBC',
                length: 256,
            },
            // extractable
            false,
            ['encrypt', 'decrypt'],
        )
    }

    return storageCryptoKey
}

/**
 * convert data as U8Int Array
 * @param data
 */
const getEncodedData = (data: string) => {
    const encoder = new TextEncoder()
    return encoder.encode(data)
}

export const saveEncrypted = async (storageKey: string, data: string) => {
    const scopedKey = scopedStorageKey(storageKey)
    if (LOCAL_WITHOUT_PWA || isIp) {
        localStorage.setItem('nonsecure_' + scopedKey, data)
        return
    }

    const key = await getStorageCryptoKey()
    const encodedData = getEncodedData(data)
    const ciphertext = await window.crypto.subtle.encrypt(
        {
            name: 'AES-CBC',
            iv,
        },
        key,
        encodedData,
    )

    const cipherTextInBase64 = btoa(
        String.fromCharCode(...new Uint8Array(ciphertext)),
    )
    localStorage.setItem(scopedKey, cipherTextInBase64)
}

export const loadDecrypted = async (storageKey: string) => {
    const scopedKey = scopedStorageKey(storageKey)
    if (LOCAL_WITHOUT_PWA || isIp) {
        return localStorage.getItem('nonsecure_' + scopedKey)
    }

    const key = await getStorageCryptoKey()
    const cipherTextInBase64 = localStorage.getItem(scopedKey)
    if (!cipherTextInBase64) return undefined

    const cipherTextInArrayBuffer = Uint8Array.from(
        atob(cipherTextInBase64),
        (c) => c.charCodeAt(0),
    )
    const decryptedData = await window.crypto.subtle.decrypt(
        {
            name: 'AES-CBC',
            iv,
        },
        key,
        cipherTextInArrayBuffer,
    )

    const decryptedText = new TextDecoder('utf8').decode(decryptedData)
    return decryptedText
}
